import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Search,
  Wallet,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/payouts")({
  head: () => ({
    meta: [
      { title: "Payout Requests — Spayzo Admin" },
      {
        name: "description",
        content: "Review and process merchant payout requests across the Spayzo platform.",
      },
    ],
  }),
  component: AdminPayouts,
});

type PayoutStatus = "pending" | "approved" | "paid" | "rejected";

type PayoutRequest = {
  id: string;
  merchant: string;
  merchantEmail: string;
  city: string;
  amount: number;
  fee: number;
  method: "Bank transfer" | "STC Pay" | "Wallet";
  account: string;
  requestedAt: string;
  status: PayoutStatus;
  reference: string;
};

const SEED: PayoutRequest[] = [
  {
    id: "PR-10241",
    merchant: "Central Coffee Co.",
    merchantEmail: "finance@centralcoffee.sa",
    city: "Riyadh",
    amount: 12480,
    fee: 62.4,
    method: "Bank transfer",
    account: "SNB •••• 4821",
    requestedAt: "2026-07-24 09:12",
    status: "pending",
    reference: "TRX-882014",
  },
  {
    id: "PR-10240",
    merchant: "Loftworks Studio",
    merchantEmail: "ops@loftworks.sa",
    city: "Jeddah",
    amount: 8320,
    fee: 41.6,
    method: "STC Pay",
    account: "STC •••• 7712",
    requestedAt: "2026-07-24 08:44",
    status: "pending",
    reference: "TRX-882013",
  },
  {
    id: "PR-10238",
    merchant: "The Hub Coworking",
    merchantEmail: "billing@thehub.sa",
    city: "Dammam",
    amount: 15720,
    fee: 78.6,
    method: "Bank transfer",
    account: "Rajhi •••• 1092",
    requestedAt: "2026-07-23 17:03",
    status: "approved",
    reference: "TRX-881942",
  },
  {
    id: "PR-10231",
    merchant: "Nomad House",
    merchantEmail: "hi@nomadhouse.sa",
    city: "Riyadh",
    amount: 4260,
    fee: 21.3,
    method: "Wallet",
    account: "Wallet balance",
    requestedAt: "2026-07-22 12:31",
    status: "paid",
    reference: "TRX-881700",
  },
  {
    id: "PR-10229",
    merchant: "Makers Loft",
    merchantEmail: "team@makersloft.sa",
    city: "Khobar",
    amount: 2150,
    fee: 10.75,
    method: "Bank transfer",
    account: "ANB •••• 3391",
    requestedAt: "2026-07-22 09:18",
    status: "rejected",
    reference: "TRX-881688",
  },
  {
    id: "PR-10221",
    merchant: "Studio Nine",
    merchantEmail: "studio@nine.sa",
    city: "Mecca",
    amount: 6890,
    fee: 34.45,
    method: "Bank transfer",
    account: "SNB •••• 8820",
    requestedAt: "2026-07-21 14:47",
    status: "paid",
    reference: "TRX-881510",
  },
];

const STATUS_META: Record<
  PayoutStatus,
  { className: string; icon: typeof Clock }
> = {
  pending: {
    className: "bg-amber-100 text-amber-800 hover:bg-amber-100",
    icon: Clock,
  },
  approved: {
    className: "bg-sky-100 text-sky-800 hover:bg-sky-100",
    icon: CheckCircle2,
  },
  paid: {
    className: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
    icon: CheckCircle2,
  },
  rejected: {
    className: "bg-rose-100 text-rose-800 hover:bg-rose-100",
    icon: XCircle,
  },
};

const TAB_KEYS: Array<"all" | PayoutStatus> = ["all", "pending", "approved", "paid", "rejected"];


const currency = (n: number) =>
  `SAR ${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function AdminPayouts() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PayoutRequest[]>(SEED);
  const [tab, setTab] = useState<"all" | PayoutStatus>("all");
  const [query, setQuery] = useState("");
  const [method, setMethod] = useState<string>("all");
  const [viewing, setViewing] = useState<PayoutRequest | null>(null);
  const [rejecting, setRejecting] = useState<PayoutRequest | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approving, setApproving] = useState<PayoutRequest | null>(null);
  const [paying, setPaying] = useState<PayoutRequest | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== "all" && r.status !== tab) return false;
      if (method !== "all" && r.method !== method) return false;
      if (!q) return true;
      return (
        r.id.toLowerCase().includes(q) ||
        r.merchant.toLowerCase().includes(q) ||
        r.merchantEmail.toLowerCase().includes(q) ||
        r.reference.toLowerCase().includes(q)
      );
    });
  }, [rows, tab, method, query]);

  const kpis = useMemo(() => {
    const pending = rows.filter((r) => r.status === "pending");
    const approved = rows.filter((r) => r.status === "approved");
    const paid = rows.filter((r) => r.status === "paid");
    const sum = (list: PayoutRequest[]) => list.reduce((a, b) => a + b.amount, 0);
    return {
      pendingCount: pending.length,
      pendingAmount: sum(pending),
      approvedAmount: sum(approved),
      paidMonth: sum(paid),
    };
  }, [rows]);

  const updateStatus = (id: string, status: PayoutStatus) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));

  const exportCsv = () => {
    const header = ["ID", "Merchant", "Email", "City", "Amount", "Fee", "Method", "Account", "Status", "Requested"];
    const body = filtered.map((r) => [
      r.id,
      r.merchant,
      r.merchantEmail,
      r.city,
      r.amount,
      r.fee,
      r.method,
      r.account,
      r.status,
      r.requestedAt,
    ]);
    const csv = [header, ...body].map((row) => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payout-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("pages.admin.payouts.exportedToast"));
  };

  const METHOD_KEY: Record<string, string> = {
    "Bank transfer": "bank",
    "STC Pay": "stc",
    Wallet: "wallet",
  };

  return (
    <AdminShell
      title={t("pages.admin.payouts.title")}
      subtitle={t("pages.admin.payouts.subtitle")}
      actions={
        <Button variant="outline" className="h-10 rounded-xl" onClick={exportCsv}>
          <Download className="h-4 w-4" /> {t("pages.admin.payouts.exportCsv")}
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t("pages.admin.payouts.kpis.pendingRequests")}
          value={String(kpis.pendingCount)}
          hint={currency(kpis.pendingAmount)}
          tint="#F59E0B"
          icon={Clock}
        />
        <KpiCard
          label={t("pages.admin.payouts.kpis.approvedAwaiting")}
          value={currency(kpis.approvedAmount)}
          hint={t("pages.admin.payouts.kpis.approvedHint")}
          tint="#0EA5E9"
          icon={ArrowUpRight}
        />
        <KpiCard
          label={t("pages.admin.payouts.kpis.paidThisMonth")}
          value={currency(kpis.paidMonth)}
          hint={t("pages.admin.payouts.kpis.paidHint")}
          tint="#10B981"
          icon={CheckCircle2}
        />
        <KpiCard
          label={t("pages.admin.payouts.kpis.platformFees")}
          value={currency(rows.reduce((a, b) => a + b.fee, 0))}
          hint={t("pages.admin.payouts.kpis.platformFeesHint")}
          tint="#5958F3"
          icon={Wallet}
        />
      </div>

      <Card className="mt-6 border-border/60 shadow-soft">
        <div className="flex flex-col gap-3 border-b border-border/60 p-4 sm:p-5 md:flex-row md:items-center md:justify-between">
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList className="h-10 rounded-xl bg-surface p-1">
              {TAB_KEYS.map((k) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  className="rounded-lg px-3 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm sm:text-sm"
                >
                  {t(`pages.admin.payouts.tabs.${k}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative min-w-0 sm:w-64">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("pages.admin.payouts.search")}
                className="h-10 rounded-xl border-border/70 bg-surface ps-9"
              />
            </div>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-10 rounded-xl border-border/70 bg-surface sm:w-44">
                <SelectValue placeholder={t("pages.admin.payouts.methods.placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.admin.payouts.methods.all")}</SelectItem>
                <SelectItem value="Bank transfer">{t("pages.admin.payouts.methods.bank")}</SelectItem>
                <SelectItem value="STC Pay">{t("pages.admin.payouts.methods.stc")}</SelectItem>
                <SelectItem value="Wallet">{t("pages.admin.payouts.methods.wallet")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-surface/50 hover:bg-surface/50">
                <TableHead className="ps-5 pe-4">{t("pages.admin.payouts.table.cols.request")}</TableHead>
                <TableHead className="px-4">{t("pages.admin.payouts.table.cols.merchant")}</TableHead>
                <TableHead className="px-4">{t("pages.admin.payouts.table.cols.method")}</TableHead>
                <TableHead className="pe-5 text-end">{t("pages.admin.payouts.table.cols.amount")}</TableHead>
                <TableHead className="px-4">{t("pages.admin.payouts.table.cols.requested")}</TableHead>
                <TableHead className="px-4">{t("pages.admin.payouts.table.cols.status")}</TableHead>
                <TableHead className="pe-5 text-end">{t("pages.admin.payouts.table.cols.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                    {t("pages.admin.payouts.table.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const meta = STATUS_META[r.status];
                  const Icon = meta.icon;
                  return (
                    <TableRow key={r.id} className="hover:bg-surface/40">
                      <TableCell className="ps-5 pe-4">
                        <div className="font-medium">{r.id}</div>
                        <div className="text-xs text-muted-foreground">{r.reference}</div>
                      </TableCell>
                      <TableCell className="px-4">
                        <div className="font-medium">{r.merchant}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.city} · {r.merchantEmail}
                        </div>
                      </TableCell>
                      <TableCell className="px-4">
                        <div className="text-sm">
                          {t(`pages.admin.payouts.methods.${METHOD_KEY[r.method] ?? "bank"}`)}
                        </div>
                        <div className="text-xs text-muted-foreground">{r.account}</div>
                      </TableCell>
                      <TableCell className="pe-5 text-end">
                        <div className="font-semibold">{currency(r.amount)}</div>
                        <div className="text-xs text-muted-foreground">
                          {t("pages.admin.payouts.table.fee", { value: currency(r.fee) })}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 text-sm text-muted-foreground">{r.requestedAt}</TableCell>
                      <TableCell className="px-4">
                        <Badge className={cn("gap-1 rounded-full border-0", meta.className)}>
                          <Icon className="h-3 w-3" /> {t(`pages.admin.payouts.status.${r.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="pe-5 text-end">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 rounded-lg"
                            onClick={() => setViewing(r)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {r.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                className="h-8 rounded-lg bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
                                onClick={() => setApproving(r)}
                              >
                                {t("pages.admin.payouts.actions.approve")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-lg text-rose-600 hover:text-rose-700"
                                onClick={() => {
                                  setRejectReason("");
                                  setRejecting(r);
                                }}
                              >
                                {t("pages.admin.payouts.actions.reject")}
                              </Button>
                            </>
                          )}
                          {r.status === "approved" && (
                            <Button
                              size="sm"
                              className="h-8 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                              onClick={() => setPaying(r)}
                            >
                              {t("pages.admin.payouts.actions.markPaid")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t("pages.admin.payouts.view.title", { id: viewing?.id ?? "" })}
            </DialogTitle>
            <DialogDescription>
              {t("pages.admin.payouts.view.sub", {
                when: viewing?.requestedAt ?? "",
                ref: viewing?.reference ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <Row label={t("pages.admin.payouts.view.merchant")} value={viewing.merchant} />
              <Row label={t("pages.admin.payouts.view.email")} value={viewing.merchantEmail} />
              <Row label={t("pages.admin.payouts.view.city")} value={viewing.city} />
              <Row
                label={t("pages.admin.payouts.view.method")}
                value={t(`pages.admin.payouts.methods.${METHOD_KEY[viewing.method] ?? "bank"}`)}
              />
              <Row label={t("pages.admin.payouts.view.account")} value={viewing.account} />
              <Row label={t("pages.admin.payouts.view.amount")} value={currency(viewing.amount)} />
              <Row label={t("pages.admin.payouts.view.platformFee")} value={currency(viewing.fee)} />
              <Row
                label={t("pages.admin.payouts.view.net")}
                value={currency(viewing.amount - viewing.fee)}
                strong
              />
              <Row
                label={t("pages.admin.payouts.view.status")}
                value={
                  <Badge className={cn("rounded-full border-0", STATUS_META[viewing.status].className)}>
                    {t(`pages.admin.payouts.status.${viewing.status}`)}
                  </Badge>
                }
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>
              {t("pages.admin.payouts.view.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Approve */}
      <AlertDialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.admin.payouts.approve.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {approving &&
                t("pages.admin.payouts.approve.desc", {
                  amount: currency(approving.amount),
                  merchant: approving.merchant,
                  method: t(
                    `pages.admin.payouts.methods.${METHOD_KEY[approving.method] ?? "bank"}`,
                  ),
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.admin.payouts.approve.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
              onClick={() => {
                if (approving) {
                  updateStatus(approving.id, "approved");
                  toast.success(t("pages.admin.payouts.approve.toast", { id: approving.id }));
                  setApproving(null);
                }
              }}
            >
              {t("pages.admin.payouts.approve.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark paid */}
      <AlertDialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.admin.payouts.paid.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {paying &&
                t("pages.admin.payouts.paid.desc", {
                  amount: currency(paying.amount),
                  merchant: paying.merchant,
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.admin.payouts.paid.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={() => {
                if (paying) {
                  updateStatus(paying.id, "paid");
                  toast.success(t("pages.admin.payouts.paid.toast", { id: paying.id }));
                  setPaying(null);
                }
              }}
            >
              {t("pages.admin.payouts.paid.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.admin.payouts.reject.title")}</DialogTitle>
            <DialogDescription>{t("pages.admin.payouts.reject.desc")}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("pages.admin.payouts.reject.placeholder")}
            className="min-h-24"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>
              {t("pages.admin.payouts.reject.cancel")}
            </Button>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={!rejectReason.trim()}
              onClick={() => {
                if (rejecting) {
                  updateStatus(rejecting.id, "rejected");
                  toast.success(t("pages.admin.payouts.reject.toast", { id: rejecting.id }));
                  setRejecting(null);
                }
              }}
            >
              {t("pages.admin.payouts.reject.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </AdminShell>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tint,
  icon: Icon,
}: {
  label: string;
  value: string;
  hint: string;
  tint: string;
  icon: typeof Clock;
}) {
  return (
    <Card className="border-border/60 p-4 shadow-soft sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className="mt-2 truncate text-2xl font-bold tracking-tight">{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        </div>
        <div
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
          style={{ backgroundColor: `${tint}1A`, color: tint }}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/40 pb-2 last:border-b-0 last:pb-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={cn("text-sm", strong && "text-base font-semibold")}>{value}</span>
    </div>
  );
}
